import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { feedback } from '@ecosystem/newron-design-system';
import ExportContextProvider from '@hooks/export/context/ExportContextProvider';
import MultiLineChartExportProvider from '@hooks/export/utilities/MultiChartExportProvider';
import InfoModalProvider from '@components/info-modal/InfoModalProvider';
import ScrollToTop from '../ScrollToTop';
import { isAuthorized } from '@utils/Permission';
import {
  BASE_RETAIL_PATH,
  COLLECTION_PATH,
  isProductsPath,
  isTradingAssistantPath,
  NOTIFICATIONS_PREFERENCES,
  RETAIL_ALLOWED_PATH,
} from '@utils/UrlUtils';
import { Unauthorized } from '@pages/unauthorized/Unauthorized';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { UseRegisterNotificationPreference } from '@components/notification/hooks/UseRegisterNotificationPreference';

const OverviewPage = lazy(() => import('@pages/overview/Overview'));
const CategoryLeaderboardPage = lazy(() => import('@pages/category-leaderboard/CategoryLeaderboard'));
const PerformanceAnalysisPage = lazy(() => import('@pages/performance-analysis/PerformanceAnalysis'));
const OpportunityReviewPage = lazy(() => import('@pages/opportunity-review/OpportunityReview'));
const ProductAnalyzerPage = lazy(() => import('@pages/product-analyzer/ProductAnalyzer'));
const ProductsComparePage = lazy(() => import('@pages/products-compare/ProductsCompare'));
const TradingAssistantPage = lazy(() => import('@pages/trading-assistant/TradingAssistant'));
const ExplorePage = lazy(() => import('@pages/explore/Explore'));

export interface AppRoutesProps {
  retailerClient: any[];
  retailKadClient: any[];
  hitlistsRetailerClient: any[];
  sellerInsightsRetailerClient: any[];
  marketPremiumRetailerClient: any[];
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  retailerClient,
  retailKadClient,
  hitlistsRetailerClient,
  sellerInsightsRetailerClient,
  marketPremiumRetailerClient,
}) => {
  const { pathname } = useLocation();
  const pagesConfig = usePageConfig();
  const { DotsLoader } = feedback;

  UseRegisterNotificationPreference();

  if (pathname.startsWith(COLLECTION_PATH) || pathname.startsWith(NOTIFICATIONS_PREFERENCES)) {
    return <div></div>;
  }

  const hasPerformance = isAuthorized(pagesConfig.clientSubscription);
  const hasKad = isAuthorized(pagesConfig.retailKadClientSubscription);
  const hasHitlists = isAuthorized(pagesConfig.hitlistsClientSubscription);
  const hasSellerInsights = isAuthorized(pagesConfig.sellerInsightsClientSubscription);
  const hasMarketPremium = isAuthorized(pagesConfig.marketPremiumClientSubscription);

  const effectiveMainClient = hasKad ? retailKadClient : retailerClient;

  let effectiveProductClient: any[] | null = null;
  if (hasHitlists) effectiveProductClient = hitlistsRetailerClient;
  else if (hasSellerInsights) effectiveProductClient = sellerInsightsRetailerClient;
  else if (hasKad) effectiveProductClient = retailKadClient;

  const effectiveMarketClient = hasMarketPremium ? marketPremiumRetailerClient : retailKadClient;

  if (isProductsPath(pathname)) {
    return renderProductsRoutes(effectiveProductClient, DotsLoader);
  }

  if (isTradingAssistantPath(pathname)) {
    return renderTradingAssistantRoutes(hasMarketPremium || hasKad, effectiveMarketClient, DotsLoader);
  }

  if (!(hasPerformance || hasKad)) {
    return <Unauthorized />;
  }

  return renderDefaultRoutes(hasPerformance, effectiveMainClient, DotsLoader);
};

export default AppRoutes;

const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ScrollToTop>
    <InfoModalProvider>
      <Suspense fallback={<feedback.DotsLoader />}>{children}</Suspense>
    </InfoModalProvider>
  </ScrollToTop>
);

function renderProductsRoutes(effectiveProductClient: any[] | null, DotsLoader: any) {
  if (!effectiveProductClient) return <Unauthorized />;
  return (
    <Wrapper>
      <Routes>
        <Route path="products" element={<Navigate to="overview" replace />} />
        <Route path={RETAIL_ALLOWED_PATH.LEADERBOARD_PRODUCTS_COMPARE} element={<ProductsComparePage />} />
        <Route
          path="products/:page/*"
          element={
            <ExportContextProvider>
              <ProductAnalyzerPage retailerClients={effectiveProductClient} />
            </ExportContextProvider>
          }
        />
        <Route path="*" element={<Unauthorized />} />
      </Routes>
    </Wrapper>
  );
}

function renderTradingAssistantRoutes(hasAccess: boolean, effectiveMarketClient: any[], DotsLoader: any) {
  if (!hasAccess) return <Unauthorized />;
  return (
    <Wrapper>
      <Routes>
        <Route index element={<Navigate to="supplier-review" replace />} />
        <Route path=":page/*" element={<TradingAssistantPage retailerClients={effectiveMarketClient} />} />
        <Route path="*" element={<Unauthorized />} />
      </Routes>
    </Wrapper>
  );
}

function renderDefaultRoutes(hasPerformance: boolean, effectiveMainClient: any[], DotsLoader: any) {
  return (
    <Wrapper>
      <Routes>
        {hasPerformance && (
          <Route
            path="overview"
            element={
              <ExportContextProvider>
                <OverviewPage retailerClients={effectiveMainClient} />
              </ExportContextProvider>
            }
          />
        )}
        <Route
          path="category-leaderboard"
          element={<Navigate to={`/${BASE_RETAIL_PATH}/explore/leaderboard`} replace />}
        />
        <Route
          path="performance-analysis"
          element={<Navigate to={`/${BASE_RETAIL_PATH}/explore/leaderboard`} replace />}
        />
        <Route path="explore">
          <Route index element={<Navigate to="leaderboard" replace />} />
          <Route
            path=":page/*"
            element={
              <ExportContextProvider>
                <MultiLineChartExportProvider>
                  <ExplorePage retailerClients={effectiveMainClient} />
                </MultiLineChartExportProvider>
              </ExportContextProvider>
            }
          />
        </Route>
        <Route
          path="products/overview"
          element={
            <ExportContextProvider>
              <CategoryLeaderboardPage retailerClients={effectiveMainClient} />
            </ExportContextProvider>
          }
        />
        <Route
          path="products/performance-analysis"
          element={
            <MultiLineChartExportProvider>
              <PerformanceAnalysisPage retailerClients={effectiveMainClient} />
            </MultiLineChartExportProvider>
          }
        />
        {hasPerformance && (
          <Route
            path="opportunity-review"
            element={
              <ExportContextProvider>
                <OpportunityReviewPage retailerClients={effectiveMainClient} />
              </ExportContextProvider>
            }
          />
        )}
        <Route path="*" element={<Navigate to={`${RETAIL_ALLOWED_PATH.RETAILER_OVERVIEW}`} replace />} />
      </Routes>
    </Wrapper>
  );
}
